import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowLeft, Instagram, ShoppingBag } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart, BRAND } from "@/lib/store";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, total, count, clear, keyOf } = useCart();
  const [view, setView] = useState("cart"); // cart | details | done
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", location: "", note: "" });
  const [orderRef, setOrderRef] = useState("");

  const payload = () =>
    items.map((i) => ({
      product_id: i.product_id, product_name: i.product_name,
      color: i.color, size: i.size, quantity: i.quantity,
    }));

  const placeOrder = async () => {
    if (!form.name || !form.phone || !form.location) {
      toast.error("Fill in your name, phone and delivery location");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders`, { ...form, items: payload() });
      setOrderRef(res.data.order_id);
      clear();
      setView("done");
    } catch (e) {
      toast.error("Could not place order. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setView("cart");
    setForm({ name: "", phone: "", location: "", note: "" });
    setOpen(false);
  };

  const igUrl = `https://instagram.com/${BRAND.instagram}`;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm" />
          <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} data-testid="cart-drawer"
            className="fixed right-0 top-0 bottom-0 z-[120] w-full sm:w-[440px] bg-[var(--bg)] border-l border-[var(--line)] flex flex-col">
            <div className="flex items-center justify-between px-6 h-20 border-b border-[var(--line)]">
              <div className="flex items-center gap-3">
                {view === "details" && (
                  <button data-testid="back-to-cart" onClick={() => setView("cart")}><ArrowLeft className="w-5 h-5" /></button>
                )}
                <h3 className="font-anton uppercase text-2xl tracking-tight">
                  {view === "cart" ? `Cart (${count})` : view === "details" ? "Your details" : "Order placed"}
                </h3>
              </div>
              <button data-testid="close-cart" onClick={resetAndClose}><X className="w-6 h-6" /></button>
            </div>

            {/* CART */}
            {view === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  {items.length === 0 ? (
                    <p className="text-[var(--text-dim)] mt-10 text-center uppercase tracking-widest text-sm">Your cart is empty</p>
                  ) : (
                    <div className="space-y-5">
                      {items.map((i) => (
                        <div key={keyOf(i)} className="flex gap-4 border-b border-[var(--line)] pb-5" data-testid={`cart-item-${keyOf(i)}`}>
                          <img src={i.image} alt={i.product_name} className="w-20 h-20 object-cover border border-[var(--line)] bg-[var(--surface)]" />
                          <div className="flex-1">
                            <div className="flex justify-between gap-2">
                              <p className="font-anton uppercase text-base tracking-tight leading-tight">{i.product_name}</p>
                              <button data-testid={`remove-${keyOf(i)}`} onClick={() => removeItem(i)} className="text-[var(--text-dim)] hover:text-[var(--accent)]"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <p className="text-[var(--text-dim)] text-sm">{i.color} / {i.size} · {i.price || 1500} KSH</p>
                            <div className="flex items-center gap-3 mt-2">
                              <button data-testid={`dec-${keyOf(i)}`} onClick={() => updateQty(i, -1)} className="w-7 h-7 border border-[var(--line)] flex items-center justify-center hover:border-white"><Minus className="w-3.5 h-3.5" /></button>
                              <span className="w-5 text-center text-sm">{i.quantity}</span>
                              <button data-testid={`inc-${keyOf(i)}`} onClick={() => updateQty(i, 1)} className="w-7 h-7 border border-[var(--line)] flex items-center justify-center hover:border-white"><Plus className="w-3.5 h-3.5" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {items.length > 0 && (
                  <div className="border-t border-[var(--line)] p-6 space-y-4">
                    <div className="flex justify-between items-baseline">
                      <span className="uppercase tracking-widest text-sm text-[var(--text-dim)]">Total</span>
                      <span className="font-anton text-3xl" data-testid="cart-total">{total} KSH</span>
                    </div>
                    <button data-testid="checkout-btn" onClick={() => setView("details")}
                      className="w-full flex items-center justify-center gap-3 bg-[var(--accent)] text-white px-6 py-4 uppercase tracking-widest text-sm font-bold hover:bg-white hover:text-black transition-colors active:scale-95">
                      <ShoppingBag className="w-5 h-5" /> Checkout
                    </button>
                  </div>
                )}
              </>
            )}

            {/* DETAILS */}
            {view === "details" && (
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <p className="text-[var(--text-dim)] text-sm">Pop in your details and place your order — we'll finish it with you on Instagram.</p>
                {[
                  { k: "name", label: "Full name", ph: "Jane Wanjiru" },
                  { k: "phone", label: "Phone", ph: "07XX XXX XXX" },
                  { k: "location", label: "Delivery location", ph: "Westlands, Nairobi" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="uppercase tracking-widest text-xs text-[var(--text-dim)] block mb-2">{f.label}</label>
                    <input data-testid={`checkout-${f.k}`} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      placeholder={f.ph} className="w-full bg-[var(--surface)] border border-[var(--line)] px-4 py-3 focus:outline-none focus:border-white text-[var(--text)]" />
                  </div>
                ))}
                <div>
                  <label className="uppercase tracking-widest text-xs text-[var(--text-dim)] block mb-2">Note (optional)</label>
                  <textarea data-testid="checkout-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    rows={2} className="w-full bg-[var(--surface)] border border-[var(--line)] px-4 py-3 focus:outline-none focus:border-white text-[var(--text)]" />
                </div>
                <div className="flex justify-between items-baseline pt-2">
                  <span className="uppercase tracking-widest text-sm text-[var(--text-dim)]">Total</span>
                  <span className="font-anton text-3xl">{total} KSH</span>
                </div>
                <button data-testid="place-order-btn" onClick={placeOrder} disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-[var(--accent)] text-white px-6 py-4 uppercase tracking-widest text-sm font-bold hover:bg-white hover:text-black transition-colors active:scale-95 disabled:opacity-50">
                  {loading ? "Placing..." : "Place order"}
                </button>
              </div>
            )}

            {/* DONE */}
            {view === "done" && (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8" data-testid="order-done">
                <div className="w-16 h-16 rounded-full bg-[var(--accent)] flex items-center justify-center mb-8">
                  <Instagram className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-anton uppercase text-4xl tracking-tight leading-none">Thanks for<br />your order!</h3>
                <p className="text-[var(--text-dim)] mt-5 max-w-xs">
                  Please message us on Instagram <span className="text-[var(--text)] font-semibold">@{BRAND.instagram}</span> to get your order completed.
                </p>
                {orderRef && <p className="text-[var(--text-dim)] text-xs mt-3 uppercase tracking-widest">Order ref: {orderRef.slice(0, 8)}</p>}
                <a href={igUrl} target="_blank" rel="noopener noreferrer" data-testid="instagram-btn"
                  className="mt-8 inline-flex items-center gap-3 bg-[var(--text)] text-black px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-[var(--accent)] hover:text-white transition-colors">
                  <Instagram className="w-5 h-5" /> Message on Instagram
                </a>
                <button onClick={resetAndClose} data-testid="done-keep-shopping" className="mt-4 text-xs uppercase tracking-widest text-[var(--text-dim)] hover:text-white transition-colors">
                  Keep shopping
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
